<template>
  <div
    v-if="editorStatusMessage"
    class="editor-status-message"
    :class="{ 'editor-status-message--fullscreen': editorIsFullscreen }"
  >
    <CometBlockMessage
      v-if="editorStatusMessage.message === 'share-confirmation'"
      block-message-type="success"
      class="editor-status-message__block"
    >
      <span role="status">
        {{ shareMessage }}
      </span>
      <template #button>
        <button
          class="comet-button comet-button--small comet-button--icon-only"
          :aria-label="$t('Close')"
          @click="dismissMessage"
        >
          <NebulaIcon
            symbol-id="x"
            class-override="comet-button__icon comet-button__icon--left
              editor-status-message__icon"
          />
        </button>
      </template>
    </CometBlockMessage>
    <CometBlockMessage
      v-if="editorStatusMessage.message === 'publish-confirmation'"
      block-message-type="success"
      class="editor-status-message__block"
    >
      <span role="status">
        {{ $t('Your creation has been added to Atlas!') }}
      </span>
      <template #button>
        <a
          class="comet-button comet-button--primary"
          :href="viewInContentUrl"
        >{{ $t('View in Atlas') }}</a>
        <button
          class="comet-button comet-button--small comet-button--icon-only"
          :aria-label="$t('Close')"
          @click="dismissMessage"
        >
          <NebulaIcon
            symbol-id="x"
            class-override="comet-button__icon comet-button__icon--left
              editor-status-message__icon"
          />
        </button>
      </template>
    </CometBlockMessage>
    <CometBlockMessage
      v-if="editorStatusMessage.message === 'editor-error'"
      block-message-type="error"
      class="editor-status-message__block"
    >
      <span role="alert">
        {{ editorStatusMessage.error }}
      </span>
      <template #button>
        <button
          class="comet-button comet-button--small comet-button--icon-only"
          :aria-label="$t('Close')"
          @click="dismissMessage"
        >
          <NebulaIcon
            symbol-id="x"
            class-override="comet-button__icon comet-button__icon--left
              editor-status-message__icon"
          />
        </button>
      </template>
    </CometBlockMessage>
    <!--
      Note: Other messages will end up getting added in the future here, like the
      success message for an editorial publish, which needs to send the user to the
      publishing queue
    -->
  </div>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { mapActions, mapState } from 'pinia';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import { getEnv } from '@/lib/api';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'EditorStatusMessage',
  components: {
    CometBlockMessage,
    NebulaIcon,
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftType',
      'draftAssetId',
      'editorStatusMessage',
      'editorIsFullscreen',
    ]),
    shareMessage() {
      if (this.draftType === 'lesson') {
        return this.$t('Your Studio lesson has been shared.');
      }

      return this.draftType === 'board'
        ? this.$t('Your Studio board has been shared.')
        : this.$t('Your Studio slideshow has been shared.');
    },
    viewInContentUrl() {
      // Studio content is authored in the US site rather than in a region-specific content site
      const currentEnv = getEnv();
      const appDomainUS = process.env[`VUE_APP_${currentEnv}_APPURL_US`];

      // Create a url like https://atlas.discoveryeducation.com/assets/<BOARD_ID>
      return appDomainUS.replace('app', 'atlas').replace('learn', `assets/${this.draftAssetId()}`);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.SET_EDITOR_STATUS_MESSAGE,
    ]),
    dismissMessage() {
      this[types.SET_EDITOR_STATUS_MESSAGE](null);
    },
  },
};
</script>

<style lang="stylus">
.editor-status-message {
  align-items: center;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  width: 100%;

  &--fullscreen {
    margin-top: $nebula-space-2x;
  }

  &__block.comet-block-message {
    margin: 0 auto;
    position: relative;
    z-index: 800;
  }

  &__icon.comet-button__icon {
    height: $comet-size-icon-m;
    width: $comet-size-icon-m;
  }
}
</style>
